import {
  ComponentIds,
  ConnectionUtils,
  Entity,
  HierarchyUtils,
  LocalTransformComponent,
  MeasurementUtils,
  SceneManager,
  Snap,
  SnapUtils,
  Transform,
  TransformUtils,
  Validation,
  Vector
} from '@inter-ikea-core/core';
import {
  CoreApi,
  DistanceMeasurementSystem,
  EketConnectionFittingsSystem,
  LegsSystem,
  Product,
  ShoppingBagComponent
} from '@inter-ikea-storageone/range';
import { EkrisComponent } from '@inter-ikea-storageone/range-eket';
import { getRandomBlock } from './utils';
import { EkrisSingleWallValidation } from './EkrisSingleWallValidation';

export class EkrisGame {
  private coreApi: CoreApi;
  private lastTime: number;
  private activeEntity: Entity | null;
  private speed: number;
  private sceneManager: SceneManager;
  private frameProducts: Product[];
  private gameStopped: boolean = false;
  private singleWallValidation: Validation | null = null;
  private onGameOver: () => void = () => {};
  private readonly EKET_SIZE = 350;
  private readonly WIDTH_MULTIPLIER = 11;
  private readonly HEIGHT_MULTIPLIER = 20;

  constructor(coreApi: CoreApi) {
    this.coreApi = coreApi;
    this.frameProducts = [];
    this.lastTime = Date.now();
    this.activeEntity = null;
    this.speed = 500;
    this.sceneManager = this.coreApi.getServices().sceneManager;
  }

  /**
   * Starts the game by initializing various systems and setting up the game environment.
   *
   * @returns {Promise<void>} A promise that resolves when the game has started.
   *
   * @remarks
   * This method performs the following actions:
   * - Disables the `DistanceMeasurementSystem`, `LegsSystem`, and `EketConnectionFittingsSystem`.
   * - Initializes and adds a `SingleWallValidation` to the core API.
   * - Sets the room size based on predefined constants.
   * - Retrieves the room entity and its bounding box.
   * - Adjusts the camera settings in the `ipexViewer` to frame the room and set specific camera parameters.
   * - Initiates the game loop using `requestAnimationFrame`.
   */
  startGame = async (frameProducts: Product[], onGameOver: () => void) => {
    this.gameStopped = false;
    this.coreApi.resetScene();
    this.onGameOver = onGameOver;
    this.frameProducts = frameProducts;
    this.coreApi.getSystem<DistanceMeasurementSystem>('DistanceMeasurementSystem')?.disable();
    this.coreApi.getSystem<LegsSystem>('LegsSystem')?.disable();
    this.coreApi.getSystem<EketConnectionFittingsSystem>('EketConnectionFittingsSystem')?.disable();
    this.singleWallValidation = new EkrisSingleWallValidation(this.coreApi);
    this.coreApi.history.disable();
    this.coreApi.core.addValidation(this.singleWallValidation);
    this.coreApi.kvadratRoom?.setRoomSize(
      this.EKET_SIZE * this.WIDTH_MULTIPLIER,
      this.EKET_SIZE * this.HEIGHT_MULTIPLIER,
      5000
    );

    const camera = this.coreApi.camera.createCamera();
    camera.frame_objects(
      this.coreApi.entities.getObjectIds(
        this.coreApi.entities.getByComponent(ComponentIds.ROOM_PART_COMPONENT_ID)
      )
    );
    this.coreApi.ipexViewer.get_camera_manager()?.replace_camera(camera);
    requestAnimationFrame(this.setupGameLoop);
  };

  restartGame = (frameProducts: Product[], onGameOver: () => void) => {
    this.sceneManager.entities
      .filter(entity => entity.hasComponent(EkrisComponent.id))
      .forEach(entity => entity.dispose());
    this.frameProducts = frameProducts;
    this.lastTime = Date.now();
    this.activeEntity = null;
    this.speed = 500;
    this.gameStopped = false;
    this.onGameOver = onGameOver;
    requestAnimationFrame(this.setupGameLoop);
  };

  stopGame = () => {
    this.gameStopped = true;
    this.sceneManager.entities
      .filter(entity => entity.hasComponent(EkrisComponent.id))
      .forEach(entity => entity.dispose());
    this.coreApi.getSystem<DistanceMeasurementSystem>('DistanceMeasurementSystem')?.enable();
    this.coreApi.getSystem<LegsSystem>('LegsSystem')?.enable();
    this.coreApi.getSystem<EketConnectionFittingsSystem>('EketConnectionFittingsSystem')?.enable();
    this.coreApi.history.enable();
    this.singleWallValidation && this.coreApi.core.removeValidation(this.singleWallValidation);
  };

  setupGameLoop = () => {
    if (this.gameStopped) return;
    const time = Date.now();
    const delta = (time - this.lastTime) / 1000;
    this.lastTime = time;
    this.update(delta);
    requestAnimationFrame(this.setupGameLoop);
  };

  update = (deltaTime: number) => {
    if (!this.activeEntity) {
      this.spawnNewBlock();
    } else {
      this.moveBlock(this.activeEntity, new Vector(0, -this.speed * deltaTime, 0));
    }
  };

  spawnNewBlock = () => {
    // Don't spawn new block if game is stopped
    if (this.gameStopped) return;

    // Don't spawn new block if there is already an active entity
    if (this.activeEntity) return;
    this.activeEntity = getRandomBlock(this.frameProducts, this.coreApi);
    if (this.activeEntity) {
      TransformUtils.setWorldTransform(
        this.activeEntity,
        new Transform(
          new Vector(0, this.EKET_SIZE * (this.HEIGHT_MULTIPLIER - 2), this.EKET_SIZE / 2)
        )
      );
      const snaps = SnapUtils.getMatchingSnapsInScene(this.activeEntity, this.sceneManager)
        .filter(snap => snap.movement.size < 10)
        .filter(snap => this.coreApi.getServices().validations.validateSnapCandidate(snap).valid)
        .sort((snap1, snap2) => snap2.priority - snap1.priority);
      if (snaps.length > 0) {
        snaps[0].performMove();
        this.coreApi.history.sendHistoryEvent();
      } else {
        const position = TransformUtils.worldPosition(this.activeEntity);
        if (position.Y >= this.EKET_SIZE * (this.HEIGHT_MULTIPLIER - 2)) {
          this.activeEntity.dispose();
          this.activeEntity = null;
          this.gameOver();
        }
      }
    }
  };

  rotateClockWise = () => {
    if (!this.activeEntity) return;
    this.rotateSubBlocks(this.activeEntity, true);
    const snaps = SnapUtils.getMatchingSnapsInScene(this.activeEntity, this.sceneManager)
      .filter(snap => {
        const activationDistance =
          snap.hostConnector.activationDistance + snap.guestConnector.activationDistance;
        return activationDistance >= snap.movement.size;
      })
      .filter(snap => this.coreApi.getServices().validations.validateSnapCandidate(snap).valid)
      .sort((snap1, snap2) => snap2.priority - snap1.priority);

    if (snaps.length > 0) {
      const topSnap = snaps[0];
      if (topSnap.priority > 0) {
        const children = HierarchyUtils.children(this.activeEntity);
        topSnap.performMoveAndConnect();
        this.checkLine(children);
        this.activeEntity = null;
      } else {
        topSnap.performMove();
      }
    } else {
      this.rotateSubBlocks(this.activeEntity, false);
    }
  };

  rotateSubBlocks = (block: Entity, clockwise: boolean) => {
    const children = HierarchyUtils.children(block);
    children.forEach(child => {
      const transform = child.getComponentUnsafe<LocalTransformComponent>(
        ComponentIds.LOCAL_TRANSFORM_COMPONENT_ID
      ).transform;
      const x = transform.position.X;
      const y = transform.position.Y;
      const newX = clockwise ? y : -y;
      const newY = clockwise ? -x : x;
      const newTransform = new Transform(new Vector(newX, newY, transform.position.Z));
      child.addComponent(new LocalTransformComponent(newTransform));
    });
  };

  moveBlock = (entity: Entity, movement: Vector, forced?: boolean) => {
    TransformUtils.moveInWorldSpace(entity, movement);
    const snaps = SnapUtils.getMatchingSnapsInScene(entity, this.sceneManager)
      .filter(snap => {
        const activationDistance =
          snap.hostConnector.activationDistance + snap.guestConnector.activationDistance;
        return activationDistance >= snap.movement.size;
      })
      .filter(snap => this.coreApi.getServices().validations.validateSnapCandidate(snap).valid)
      .sort((snap1, snap2) => snap2.priority - snap1.priority);

    if (snaps.length > 0) {
      const topSnap = snaps[0];
      if (topSnap.priority > 0) {
        if (forced) {
          topSnap.performMoveAndConnect();
        } else {
          this.handleBlockLanding(topSnap);
        }
      } else {
        topSnap.performMove();
      }
    } else {
      TransformUtils.moveInWorldSpace(entity, movement.times(-1));
    }
  };

  handleBlockLanding = (snapCandidate: Snap) => {
    const children = HierarchyUtils.children(snapCandidate.guestRoot);
    snapCandidate.performMoveAndConnect();
    this.checkLine(children);
    this.activeEntity = null;
  };

  moveRight = () => {
    if (this.activeEntity) {
      this.moveBlock(this.activeEntity, new Vector(this.EKET_SIZE, 0, 0));
    }
  };

  moveLeft = () => {
    if (this.activeEntity) {
      this.moveBlock(this.activeEntity, new Vector(-this.EKET_SIZE, 0, 0));
    }
  };

  moveDown = () => {
    if (this.activeEntity) {
      this.moveBlock(this.activeEntity, new Vector(0, -this.EKET_SIZE, 0));
    }
  };

  hardDrop = () => {
    if (this.activeEntity && !this.gameStopped) {
      const children = HierarchyUtils.children(this.activeEntity);
      let shortestDistance = 10000;
      children.forEach(child => {
        const result = MeasurementUtils.getInsideMeasurementWorldSpace(
          child,
          this.sceneManager.scene,
          new Vector(0, -1, 0)
        );
        if (
          result?.end.entity &&
          HierarchyUtils.parent(result.end.entity)?.id === this.activeEntity?.id
        ) {
          return;
        }
        if (result && result?.distance < shortestDistance) {
          shortestDistance = result.distance;
        }
      });
      this.moveBlock(this.activeEntity, new Vector(0, -shortestDistance, 0));
    }
  };

  checkLine = (children: Entity[]) => {
    children.forEach(child => {
      const yPosition = TransformUtils.worldPosition(child).Y;
      const allOnSameHeight = this.sceneManager.entities.filter(
        e =>
          e.hasComponent(ComponentIds.FRAME_COMPONENT_ID) &&
          Math.abs(TransformUtils.worldPosition(e).Y - yPosition) < 10
      );

      if (allOnSameHeight.length < this.WIDTH_MULTIPLIER) return;

      this.speed += 70;
      allOnSameHeight.forEach(e => {
        const frameChildren = HierarchyUtils.children(e).filter(child =>
          child.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
        );
        frameChildren.forEach(child => {
          ConnectionUtils.disconnect(child);
        });
        this.createFakeEntity(e.getComponentUnsafe(ShoppingBagComponent.id));
        e.dispose();

        frameChildren.forEach(child => {
          this.moveBlock(child, new Vector(0, -this.EKET_SIZE, 0), true);
        });
      });
    });
  };

  createFakeEntity = (component: ShoppingBagComponent) => {
    const entity = this.coreApi.getServices().entityManager.createEntity();
    entity.addComponent(new EkrisComponent());
    HierarchyUtils.setParent(entity, this.sceneManager.scene);
    entity.addComponent(component);
  };

  gameOver = () => {
    this.onGameOver();
    this.gameStopped = true;
  };
}
